import { Inject, Injectable } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateService } from '@ngx-translate/core';
import Env from '../../env';

@Injectable({ providedIn: 'root' })
export class AmountAnnouncerService {
    constructor(
        private liveAnnouncer: LiveAnnouncer,
        private translate: TranslateService,
        @Inject('ENV') private env: Env
    ) {}

    async announceAmountAndTotal(count: number, total: number, currency?: string): Promise<void> {
        try {
            const labelAmount = this.translate.instant('AMOUNT');
            const labelTotal = this.translate.instant('TOTAL');
            const currencyValue = this.translate.instant(this.env.CURRENCY || currency || '');
            const formattedPrice = this.formatPrice(total);

            const message = `${labelAmount} ${count}. ${labelTotal} ${formattedPrice} ${currencyValue}.`;

            this.liveAnnouncer.announce(message, 'polite');
        } catch {
            // Silently ignore announcement failures
        }
    }

    public formatPrice(total: number | string): string {
        const rawValue = Number(total) || 0;
        const adjustedValue = this.env.IS_NEW_AFC ? rawValue / 100 : rawValue;

        return adjustedValue.toLocaleString(this.translate.currentLang, {
            minimumFractionDigits: this.env?.MIN_DECIMAL_DIGITS ?? 2,
            maximumFractionDigits: 2
        });
    }

    public getCurrencyForDisplay(...currencies: (string | undefined)[]): string {
        return [this.env?.CURRENCY, ...currencies].find((c) => !!c) || '';
    }

    public buildPriceWithCurrency(amount: number, ...currencies: (string | undefined)[]): string {
        const formatted = this.formatPrice(amount);
        const currency = this.getCurrencyForDisplay(...currencies);
        return `${formatted} ${currency}`.trim();
    }

    public resolveAmountValue(price?: { amount?: number | string; maxAmount?: number | string } | null): number {
        const val = price?.amount ?? price?.maxAmount ?? 0;
        const num = Number(val);
        return Number.isFinite(num) ? num : 0;
    }

    public buildProductAriaLabel(params: {
        name: string;
        description?: string | null;
        amount: number;
        priceCurrency?: string;
        fallbackCurrency?: string;
    }): string {
        const { name, description, amount, priceCurrency, fallbackCurrency } = params;
        const price = this.buildPriceWithCurrency(amount, priceCurrency, fallbackCurrency);

        return [name, description, price].filter((part) => !!part && String(part).trim() !== '').join(', ');
    }
}
